/**
 * ProductHeroTopBar — kompakte Kopfzeile des Produkt-Hero.
 * Links: Status-/Provider-/Kategorie-Badges. Rechts: Erstellt/Sync-Meta + Aktionen.
 * Max 100 lines.
 */

import { useTranslation } from 'react-i18next';
import { RefreshCw, MoreVertical } from 'lucide-react';
import type { Product } from '@/types/products';
import { Button, Badge } from '@/components/v2/ui';
import { EID } from '@/lib/element-ids.registry';

interface ProductHeroTopBarProps {
  product: Product;
  isSyncing: boolean;
  onSync: () => void;
}

function formatDate(dateStr: string | null, locale: string): string {
  if (!dateStr) return '–';
  return new Date(dateStr).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatRelative(dateStr: string | null, locale: string): string | null {
  if (!dateStr) return null;
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });
  const days = Math.round(diffMs / 86_400_000);
  if (Math.abs(days) >= 1) return rtf.format(-days, 'day');
  const hours = Math.round(diffMs / 3_600_000);
  if (Math.abs(hours) >= 1) return rtf.format(-hours, 'hour');
  return rtf.format(-Math.round(diffMs / 60_000), 'minute');
}

export function ProductHeroTopBar({ product, isSyncing, onSync }: ProductHeroTopBarProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'de';
  const syncRelative = formatRelative(product.synced_at, locale);

  return (
    <div className="product-hero-topbar flex items-start justify-between gap-3 flex-wrap">
      <div className="product-hero-topbar-badges flex items-center gap-2 flex-wrap">
        <Badge variant={product.is_active ? 'success' : 'secondary'} className="gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${product.is_active ? 'bg-green-500 animate-pulse' : 'bg-neutral-400'}`} />
          {product.is_active ? t('common.active') : t('common.inactive')}
        </Badge>
        <Badge variant="info">{(product.provider_type ?? 'JTL').toUpperCase()}</Badge>
        {product.category && <Badge variant="secondary">{product.category}</Badge>}
      </div>

      <div className="product-hero-topbar-actions flex items-center gap-3">
        <span className="text-xs text-neutral-500 dark:text-neutral-400 hidden sm:inline">
          {t('products.created')} {formatDate(product.created_at, locale)}
          {syncRelative && <> · {t('products.hero.lastSync')} {syncRelative}</>}
        </span>
        <Button
          id={EID['product-hero-more-actions-btn']}
          variant="ghost"
          size="sm"
          aria-label={t('common.moreActions')}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
        <Button
          id={EID['product-hero-sync-btn']}
          variant="primary"
          size="sm"
          onClick={onSync}
          disabled={isSyncing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
          {isSyncing ? t('common.loading') : t('common.sync')}
        </Button>
      </div>
    </div>
  );
}
